import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Building2, 
  Users, 
  TrendingUp, 
  CreditCard,
  Download,
  Bell,
  Loader2,
  FileSpreadsheet,
  FileText,
  Calendar,
  Crown,
  Zap,
  AlertTriangle,
  CheckCircle,
  Info,
  RefreshCw,
  BarChart3,
  PieChart,
  Activity,
  Gift,
  Settings,
  Ban,
  Unlock,
  Eye,
  History,
  TrendingDown,
  DollarSign,
  UserCheck,
  UserX,
  LineChart,
  Target,
  Send,
  Mail,
  Clock,
  AlertCircle,
  HardDrive,
  Database,
  Trash2,
  Receipt,
  Handshake,
  Plus,
  Edit,
  UserPlus,
  Percent,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Plan badge colors - All 6 plans
const planColors = {
  free: 'bg-slate-100 text-slate-700',
  premium: 'bg-amber-100 text-amber-700',
  governance: 'bg-indigo-100 text-indigo-700',
  pro: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-emerald-100 text-emerald-700',
  convenzioni: 'bg-rose-100 text-rose-700'
};

// Plan display names
const planNames = {
  free: 'Free',
  premium: 'Premium',
  governance: 'Governance',
  pro: 'Pro',
  enterprise: 'Enterprise',
  convenzioni: 'Convenzioni'
};

// Plan prices for display (IVA inclusa)
const planPrices = {
  free: '€0',
  premium: '€9,99/mese',
  governance: '€14,99/mese',
  pro: '€24,99/mese',
  enterprise: '€49,99/mese',
  convenzioni: 'Custom'
};

const statusColors = {
  active: 'bg-green-100 text-green-700',
  expired: 'bg-red-100 text-red-700',
  trial: 'bg-blue-100 text-blue-700'
};

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState(null);
  const [enti, setEnti] = useState([]);
  const [notifiche, setNotifiche] = useState([]);
  const [abbonamenti, setAbbonamenti] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [adminLogs, setAdminLogs] = useState([]);
  const [selectedEnte, setSelectedEnte] = useState(null);
  const [enteDetail, setEnteDetail] = useState(null);
  const [showEnteModal, setShowEnteModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Analytics state
  const [growthData, setGrowthData] = useState(null);
  const [retentionData, setRetentionData] = useState(null);
  const [featureUsageData, setFeatureUsageData] = useState(null);
  const [plansData, setPlansData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Retention/Re-engagement state
  const [dormantEnti, setDormantEnti] = useState([]);
  const [retentionStats, setRetentionStats] = useState(null);
  
  // Storage monitoring state
  const [storageData, setStorageData] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [dormancyThreshold, setDormancyThreshold] = useState(30);
  
  // Convenzione state
  const [convenzioneForm, setConvenzioneForm] = useState({
    has_convenzione: false,
    convenzione_nome: '',
    tipo_sconto: 'percentuale',
    sconto_percentuale: '',
    prezzo_fisso: '',
    convenzione_scadenza: '',
    convenzione_note: ''
  });
  const [savingConvenzione, setSavingConvenzione] = useState(false);
  
  // Note Admin state
  const [adminNote, setAdminNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  
  // Storico Pagamenti state
  const [storicoPayments, setStoricoPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  
  // Convenzioni Nazionali state
  const [convenzioniNazionali, setConvenzioniNazionali] = useState([]);
  const [convenzioniLoading, setConvenzioniLoading] = useState(false);
  const [convenzioniReport, setConvenzioniReport] = useState(null);
  const [showNewConvenzioneModal, setShowNewConvenzioneModal] = useState(false);
  const [showConvenzioneEntiModal, setShowConvenzioneEntiModal] = useState(null);
  const [convenzioneEntiList, setConvenzioneEntiList] = useState([]);
  const [newConvenzioneForm, setNewConvenzioneForm] = useState({
    nome: '',
    descrizione: '',
    tipo_sconto: 'percentuale',
    sconto_percentuale: '',
    prezzo_fisso: '',
    revenue_sharing: '',
    fee_nazionale: '',
    data_inizio: new Date().toISOString().split('T')[0],
    data_scadenza: '',
    max_enti: '',
    note: '',
    attiva: true
  });
  const [savingNewConvenzione, setSavingNewConvenzione] = useState(false);
  
  // Filters
  const [pianoFilter, setPianoFilter] = useState('all');
  const [regioneFilter, setRegioneFilter] = useState('');
  const [naturaFilter, setNaturaFilter] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    fetchEnti();
  }, [pianoFilter, regioneFilter, naturaFilter]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchStats(),
      fetchTrend(),
      fetchEnti(),
      fetchNotifiche(),
      fetchAbbonamenti(),
      fetchAdminLogs()
    ]);
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard/stats`, { credentials: 'include' });
      if (response.ok) {
        setStats(await response.json());
      } else if (response.status === 403) {
        toast.error('Accesso non autorizzato. Solo per amministratori piattaforma.');
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchTrend = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard/registrazioni?mesi=12`, { credentials: 'include' });
      if (response.ok) {
        setTrend(await response.json());
      }
    } catch (error) {
      console.error('Error fetching trend:', error);
    }
  };

  const fetchEnti = async () => {
    try {
      let url = `${API_URL}/api/admin/dashboard/enti?limit=100`;
      if (pianoFilter && pianoFilter !== 'all') url += `&piano=${pianoFilter}`;
      if (regioneFilter) url += `&regione=${regioneFilter}`;
      if (naturaFilter) url += `&natura_giuridica=${naturaFilter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEnti(data.enti || []);
      }
    } catch (error) {
      console.error('Error fetching enti:', error);
    }
  };

  const fetchNotifiche = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard/notifiche`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setNotifiche(data.notifiche || []);
      }
    } catch (error) {
      console.error('Error fetching notifiche:', error);
    }
  };

  const fetchAbbonamenti = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard/abbonamenti`, { credentials: 'include' });
      if (response.ok) {
        setAbbonamenti(await response.json());
      }
    } catch (error) {
      console.error('Error fetching abbonamenti:', error);
    }
  };

  const fetchAdminLogs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/logs?limit=50`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAdminLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching admin logs:', error);
    }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const [growthRes, retentionRes, featureRes, plansRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/analytics/growth?months=12`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/analytics/retention`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/analytics/feature-usage?days=90`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/analytics/plans`, { credentials: 'include' })
      ]);
      
      if (growthRes.ok) setGrowthData(await growthRes.json());
      if (retentionRes.ok) setRetentionData(await retentionRes.json());
      if (featureRes.ok) setFeatureUsageData(await featureRes.json());
      if (plansRes.ok) setPlansData(await plansRes.json());
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchRetentionData = async () => {
    try {
      const [dormantRes, statsRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/retention/dormant-enti?days=${dormancyThreshold}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/retention/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/retention/notification-history?limit=50`, { credentials: 'include' })
      ]);
      
      if (dormantRes.ok) {
        const data = await dormantRes.json();
        setDormantEnti(data.enti || []);
      }
      if (statsRes.ok) setRetentionStats(await statsRes.json());
      if (historyRes.ok) {
        const data = await historyRes.json();
        setNotificationHistory(data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching retention data:', error);
    }
  };

  const sendReengagementEmail = async (enteId, enteName) => {
    setSendingEmail(true);
    try {
      const response = await fetch(`${API_URL}/api/retention/send-reengagement/${enteId}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchRetentionData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore invio email');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSendingEmail(false);
    }
  };

  const sendBulkReengagement = async () => {
    if (!confirm(`Inviare email di re-engagement a tutti gli enti dormienti (max 50)?`)) return;
    
    setSendingEmail(true);
    try {
      const response = await fetch(`${API_URL}/api/retention/send-bulk-reengagement?days_threshold=${dormancyThreshold}&max_emails=50`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.inviati} email inviate, ${data.saltati} saltate`);
        fetchRetentionData();
      } else {
        toast.error('Errore invio bulk');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSendingEmail(false);
    }
  };

  const fetchStorageData = async () => {
    setStorageLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/storage`, { credentials: 'include' });
      if (response.ok) {
        setStorageData(await response.json());
      } else {
        toast.error('Errore caricamento dati storage');
      }
    } catch (error) {
      console.error('Error fetching storage data:', error);
      toast.error('Errore di connessione');
    } finally {
      setStorageLoading(false);
    }
  };

  const fetchEnteDetail = async (enteId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/ente-detail/${enteId}`, { 
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setEnteDetail(data);
        setShowEnteModal(true);
        
        // Initialize convenzione form with existing data
        const ente = data.ente || {};
        setConvenzioneForm({
          has_convenzione: ente.has_convenzione || false,
          convenzione_nome: ente.convenzione_nome || '',
          tipo_sconto: ente.tipo_sconto || 'percentuale',
          sconto_percentuale: ente.sconto_percentuale || '',
          prezzo_fisso: ente.prezzo_fisso || '',
          convenzione_scadenza: ente.convenzione_scadenza || '',
          convenzione_note: ente.convenzione_note || ''
        });
        
        // Initialize admin note
        setAdminNote(ente.admin_note || '');
        
        // Load storico pagamenti
        fetchStoricoPayments(enteId);
      } else {
        const error = await response.json().catch(() => ({}));
        console.error('Error response:', error);
        toast.error(error.detail || 'Errore nel caricamento dettagli ente');
      }
    } catch (error) {
      console.error('Error fetching ente detail:', error);
      toast.error('Errore nel caricamento dettagli ente');
    }
  };

  const fetchStoricoPayments = async (enteId) => {
    setLoadingPayments(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/ente-detail/${enteId}/payments`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStoricoPayments(data.payments || []);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const changePlan = async (enteId, newPlan, motivo) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/ente-detail/${enteId}/piano`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          piano: newPlan,
          motivo: motivo
        })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchEnti();
        fetchAdminLogs();
        if (enteDetail) {
          fetchEnteDetail(enteId);
        }
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail?.message || error.detail || 'Errore nel cambio piano');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleBlockEnte = async (enteId, block, motivo) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/ente-detail/${enteId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: block ? 'block' : 'unblock', motivo })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchEnti();
        fetchAdminLogs();
        if (enteDetail) {
          fetchEnteDetail(enteId);
        }
      } else {
        toast.error('Errore nell\'operazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setActionLoading(false);
    }
  };

  const saveConvenzione = async (enteId) => {
    setSavingConvenzione(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/ente-detail/${enteId}/convenzione`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(convenzioneForm)
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Convenzione salvata');
        fetchEnteDetail(enteId);
        fetchAdminLogs();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      console.error('Error saving convenzione:', error);
      toast.error('Errore di connessione');
    } finally {
      setSavingConvenzione(false);
    }
  };

  const saveAdminNote = async (enteId) => {
    setSavingNote(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/ente-detail/${enteId}/note`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ admin_note: adminNote })
      });
      if (response.ok) {
        toast.success('Nota salvata');
        fetchAdminLogs();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Errore di connessione');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteEnte = async (enteId, enteName) => {
    // Double confirmation for dangerous action
    const confirmed = window.confirm(
      `⚠️ ATTENZIONE: Stai per eliminare definitivamente l'ente "${enteName}".\n\n` +
      `Questa azione eliminerà:\n` +
      `- Tutti i volontari\n` +
      `- Tutti i soci\n` +
      `- Tutti gli eventi e presenze\n` +
      `- Tutti i documenti\n` +
      `- Tutti i dati economici\n` +
      `- L'utente associato\n\n` +
      `L'operazione è IRREVERSIBILE.\n\n` +
      `Sei sicuro di voler procedere?`
    );
    
    if (!confirmed) return;
    
    // Second confirmation
    const doubleConfirmed = window.confirm(
      `ULTIMA CONFERMA: Scrivi "ELIMINA" per confermare la cancellazione di "${enteName}"`
    );
    
    if (!doubleConfirmed) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/ente/${enteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setShowEnteModal(false);
        setEnteDetail(null);
        // Refresh the list
        fetchEnti();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore durante l\'eliminazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setActionLoading(false);
    }
  };

  // ============= CONVENZIONI NAZIONALI FUNCTIONS =============
  
  const fetchConvenzioniNazionali = async () => {
    setConvenzioniLoading(true);
    try {
      const [listRes, reportRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/convenzioni?solo_attive=false`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/convenzioni/report`, { credentials: 'include' })
      ]);
      
      if (listRes.ok) {
        const data = await listRes.json();
        setConvenzioniNazionali(data.convenzioni || []);
      }
      if (reportRes.ok) {
        setConvenzioniReport(await reportRes.json());
      }
    } catch (error) {
      console.error('Error fetching convenzioni:', error);
      toast.error('Errore nel caricamento convenzioni');
    } finally {
      setConvenzioniLoading(false);
    }
  };

  const createConvenzioneNazionale = async () => {
    if (!newConvenzioneForm.nome) {
      toast.error('Inserisci il nome della convenzione');
      return;
    }
    
    setSavingNewConvenzione(true);
    try {
      const payload = {
        ...newConvenzioneForm,
        sconto_percentuale: newConvenzioneForm.sconto_percentuale ? parseFloat(newConvenzioneForm.sconto_percentuale) : null,
        prezzo_fisso: newConvenzioneForm.prezzo_fisso ? parseFloat(newConvenzioneForm.prezzo_fisso) : null,
        revenue_sharing: newConvenzioneForm.revenue_sharing ? parseFloat(newConvenzioneForm.revenue_sharing) : null,
        fee_nazionale: newConvenzioneForm.fee_nazionale ? parseFloat(newConvenzioneForm.fee_nazionale) : null,
        max_enti: newConvenzioneForm.max_enti ? parseInt(newConvenzioneForm.max_enti) : null
      };
      
      const response = await fetch(`${API_URL}/api/admin/convenzioni`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setShowNewConvenzioneModal(false);
        setNewConvenzioneForm({
          nome: '', descrizione: '', tipo_sconto: 'percentuale',
          sconto_percentuale: '', prezzo_fisso: '', revenue_sharing: '',
          fee_nazionale: '', data_inizio: new Date().toISOString().split('T')[0],
          data_scadenza: '', max_enti: '', note: '', attiva: true
        });
        fetchConvenzioniNazionali();
        fetchAdminLogs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nella creazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSavingNewConvenzione(false);
    }
  };

  const deleteConvenzione = async (convenzioneId, nome) => {
    if (!window.confirm(`Sei sicuro di voler eliminare/disattivare la convenzione "${nome}"?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/convenzioni/${convenzioneId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchConvenzioniNazionali();
      } else {
        toast.error('Errore nell\'eliminazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const fetchConvenzioneEnti = async (convenzioneId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/convenzioni/${convenzioneId}/enti`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setConvenzioneEntiList(data.enti || []);
        setShowConvenzioneEntiModal(data.convenzione);
      }
    } catch (error) {
      toast.error('Errore nel caricamento enti');
    }
  };

  const associaEnteConvenzione = async (convenzioneId, enteId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/convenzioni/${convenzioneId}/associa-ente/${enteId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchConvenzioniNazionali();
        fetchConvenzioneEnti(convenzioneId);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const rimuoviEnteConvenzione = async (convenzioneId, enteId) => {
    if (!window.confirm('Rimuovere questo ente dalla convenzione?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/convenzioni/${convenzioneId}/rimuovi-ente/${enteId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchConvenzioniNazionali();
        fetchConvenzioneEnti(convenzioneId);
      } else {
        toast.error('Errore nella rimozione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const exportData = async (formato) => {
    setExporting(true);
    try {
      let url = `${API_URL}/api/admin/dashboard/export?formato=${formato}`;
      if (pianoFilter && pianoFilter !== 'all') url += `&piano=${pianoFilter}`;
      if (regioneFilter) url += `&regione=${regioneFilter}`;
      if (naturaFilter) url += `&natura_giuridica=${naturaFilter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `enti_export_${new Date().toISOString().split('T')[0]}.${formato === 'excel' ? 'xlsx' : 'csv'}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success(`Export ${formato.toUpperCase()} completato`);
      }
    } catch (error) {
      toast.error('Errore durante l\'export');
    } finally {
      setExporting(false);
    }
  };

  const downloadGuidaCliente = async () => {
    try {
      toast.info('Generazione PDF in corso...');
      const response = await fetch(`${API_URL}/api/guida-cliente/pdf`, { credentials: 'include' });
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `AssieticaGest_Guida_Avvio_Rapido_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('Guida PDF scaricata con successo!');
      } else {
        toast.error('Errore nel download della guida');
      }
    } catch (error) {
      console.error('Error downloading guida:', error);
      toast.error('Errore di connessione');
    }
  };

  const getNotificaIcon = (tipo) => {
    switch (tipo) {
      case 'nuova_registrazione': return <Building2 className="w-4 h-4 text-blue-500" />;
      case 'nuovo_abbonamento': return <Crown className="w-4 h-4 text-amber-500" />;
      case 'abbonamento_scaduto': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default: return <Info className="w-4 h-4 text-slate-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055BB]" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-16">
        <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-700">Accesso non autorizzato</h2>
        <p className="text-slate-500 mt-2">Questa sezione è riservata agli amministratori della piattaforma</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Dashboard Amministratore
          </h1>
          <p className="text-slate-500 mt-1">
            Monitoraggio piattaforma Assietica Gest
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={downloadGuidaCliente} variant="outline" data-testid="download-guida-btn">
            <FileText className="w-4 h-4 mr-2" />
            Guida Onboarding PDF
          </Button>
          <Button onClick={fetchAllData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna Dati
          </Button>
        </div>
      </div>

      {/* SEZIONE 1: Contatori Principali */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">Enti Registrati</p>
                <p className="text-3xl font-bold">{stats?.contatori?.totale_enti || 0}</p>
              </div>
              <Building2 className="w-10 h-10 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">Nuovi (Mese)</p>
                <p className="text-3xl font-bold">{stats?.contatori?.nuove_registrazioni_mese || 0}</p>
              </div>
              <TrendingUp className="w-10 h-10 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">Utenti Attivi</p>
                <p className="text-3xl font-bold">{stats?.contatori?.totale_utenti || 0}</p>
              </div>
              <Users className="w-10 h-10 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">MRR Stimato</p>
                <p className="text-3xl font-bold">€{stats?.contatori?.mrr_stimato || 0}</p>
              </div>
              <DollarSign className="w-10 h-10 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* SEZIONE 1b: Distribuzione Piani */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Free</p>
                <p className="text-xl font-bold text-slate-700">{stats?.contatori?.enti_free || 0}</p>
              </div>
              <Gift className="w-6 h-6 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600">Premium</p>
                <p className="text-xl font-bold text-amber-700">{stats?.contatori?.enti_premium || 0}</p>
              </div>
              <Crown className="w-6 h-6 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-indigo-600">Governance</p>
                <p className="text-xl font-bold text-indigo-700">{stats?.contatori?.enti_governance || 0}</p>
              </div>
              <Building2 className="w-6 h-6 text-indigo-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600">Pro</p>
                <p className="text-xl font-bold text-purple-700">{stats?.contatori?.enti_pro || 0}</p>
              </div>
              <Zap className="w-6 h-6 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600">Enterprise</p>
                <p className="text-xl font-bold text-emerald-700">{stats?.contatori?.enti_enterprise || 0}</p>
              </div>
              <Target className="w-6 h-6 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-rose-50 border-rose-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-rose-600">Convenzioni</p>
                <p className="text-xl font-bold text-rose-700">{stats?.contatori?.enti_convenzioni || 0}</p>
              </div>
              <Users className="w-6 h-6 text-rose-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-10">
          <TabsTrigger value="overview">Panoramica</TabsTrigger>
          <TabsTrigger value="analytics" onClick={() => !growthData && fetchAnalytics()}>Analytics</TabsTrigger>
          <TabsTrigger value="retention" onClick={() => !retentionStats && fetchRetentionData()}>Retention</TabsTrigger>
          <TabsTrigger value="enti">Enti</TabsTrigger>
          <TabsTrigger value="convenzioni" onClick={() => convenzioniNazionali.length === 0 && fetchConvenzioniNazionali()}>Convenzioni</TabsTrigger>
          <TabsTrigger value="gestione">Gestione</TabsTrigger>
          <TabsTrigger value="piani">Piani</TabsTrigger>
          <TabsTrigger value="utilizzo">Utilizzo</TabsTrigger>
          <TabsTrigger value="storage" onClick={() => !storageData && fetchStorageData()}>Storage</TabsTrigger>
          <TabsTrigger value="notifiche">Notifiche</TabsTrigger>
        </TabsList>

        {/* TAB: PANORAMICA - Andamento Registrazioni */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* SEZIONE 2: Andamento Registrazioni */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#0055BB]" />
                  Andamento Registrazioni
                </CardTitle>
                <CardDescription>Registrazioni per mese negli ultimi 12 mesi</CardDescription>
              </CardHeader>
              <CardContent>
                {trend?.trend && (
                  <div className="space-y-3">
                    {trend.trend.slice(-6).map((item, idx) => {
                      const maxReg = Math.max(...trend.trend.map(t => t.registrazioni), 1);
                      const percentage = (item.registrazioni / maxReg) * 100;
                      return (
                        <div key={idx} className="flex items-center gap-3">
                          <span className="w-20 text-sm text-slate-500">{item.mese}</span>
                          <div className="flex-1 bg-slate-100 rounded-full h-6">
                            <div 
                              className="bg-[#0055BB] h-6 rounded-full flex items-center justify-end pr-3 transition-all"
                              style={{ width: `${Math.max(percentage, 15)}%` }}
                            >
                              <span className="text-xs text-white font-medium">{item.registrazioni}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Crescita Cumulativa */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#008A55]" />
                  Crescita Enti
                </CardTitle>
                <CardDescription>Totale cumulativo enti registrati</CardDescription>
              </CardHeader>
              <CardContent>
                {trend?.trend && (
                  <div className="space-y-3">
                    {trend.trend.slice(-6).map((item, idx) => {
                      const maxCum = trend.trend[trend.trend.length - 1]?.totale_cumulativo || 1;
                      const percentage = (item.totale_cumulativo / maxCum) * 100;
                      return (
                        <div key={idx} className="flex items-center gap-3">
                          <span className="w-20 text-sm text-slate-500">{item.mese}</span>
                          <div className="flex-1 bg-slate-100 rounded-full h-6">
                            <div 
                              className="bg-[#008A55] h-6 rounded-full flex items-center justify-end pr-3 transition-all"
                              style={{ width: `${percentage}%` }}
                            >
                              <span className="text-xs text-white font-medium">{item.totale_cumulativo}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB: ANALYTICS */}
        <TabsContent value="analytics" className="mt-6">
          {analyticsLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#0055BB]" />
              <span className="ml-2 text-slate-500">Caricamento analytics...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* KPI Row */}
              <div className="grid gap-4 md:grid-cols-4">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-80">Tasso Conversione</p>
                        <p className="text-3xl font-bold">{plansData?.metriche?.tasso_conversione || 0}%</p>
                        <p className="text-xs opacity-70 mt-1">Free → Pagante</p>
                      </div>
                      <Target className="w-10 h-10 opacity-80" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-80">Revenue Mensile</p>
                        <p className="text-3xl font-bold">€{plansData?.metriche?.revenue_mensile_stimato || 0}</p>
                        <p className="text-xs opacity-70 mt-1">Stimato</p>
                      </div>
                      <DollarSign className="w-10 h-10 opacity-80" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-80">Retention 30g</p>
                        <p className="text-3xl font-bold">{retentionData?.tasso_retention_30g || 0}%</p>
                        <p className="text-xs opacity-70 mt-1">Utenti attivi</p>
                      </div>
                      <UserCheck className="w-10 h-10 opacity-80" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-80">Email Verificate</p>
                        <p className="text-3xl font-bold">{retentionData?.utenti?.tasso_verifica || 0}%</p>
                        <p className="text-xs opacity-70 mt-1">{retentionData?.utenti?.email_verificata || 0} utenti</p>
                      </div>
                      <CheckCircle className="w-10 h-10 opacity-80" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Growth Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <LineChart className="w-5 h-5 text-[#0055BB]" />
                      Crescita Enti
                    </CardTitle>
                    <CardDescription>Registrazioni negli ultimi 12 mesi</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {growthData?.dati_mensili && (
                      <div className="space-y-2">
                        {growthData.dati_mensili.slice(-6).map((item, idx) => {
                          const maxVal = Math.max(...growthData.dati_mensili.map(d => d.nuovi_enti), 1);
                          const percentage = (item.nuovi_enti / maxVal) * 100;
                          return (
                            <div key={idx} className="flex items-center gap-3">
                              <span className="text-sm text-slate-500 w-20">{item.mese_label}</span>
                              <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                                <div 
                                  className="bg-gradient-to-r from-blue-500 to-blue-400 h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                                  style={{ width: `${Math.max(percentage, 10)}%` }}
                                >
                                  <span className="text-xs font-medium text-white">{item.nuovi_enti}</span>
                                </div>
                              </div>
                              <span className="text-xs text-slate-400 w-16">Tot: {item.totale_cumulativo}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="mt-4 pt-4 border-t text-center">
                      <p className="text-2xl font-bold text-[#0055BB]">{growthData?.totale_attuale || 0}</p>
                      <p className="text-sm text-slate-500">Enti totali registrati</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Plan Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-[#0055BB]" />
                      Distribuzione Piani
                    </CardTitle>
                    <CardDescription>Abbonamenti attivi per tipologia</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {plansData?.distribuzione_piani && (
                      <div className="space-y-4">
                        {Object.entries(plansData.distribuzione_piani).map(([plan, count]) => {
                          const total = Object.values(plansData.distribuzione_piani).reduce((a, b) => a + b, 0);
                          const percentage = total > 0 ? (count / total * 100).toFixed(1) : 0;
                          const colors = {
                            free: 'from-slate-400 to-slate-500',
                            premium: 'from-amber-400 to-amber-500',
                            pro: 'from-purple-400 to-purple-500'
                          };
                          return (
                            <div key={plan}>
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-sm font-medium capitalize">{plan}</span>
                                <span className="text-sm text-slate-500">{count} ({percentage}%)</span>
                              </div>
                              <div className="w-full bg-slate-100 rounded-full h-4">
                                <div 
                                  className={`bg-gradient-to-r ${colors[plan]} h-full rounded-full transition-all duration-500`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="mt-6 grid grid-cols-2 gap-4">
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <p className="text-xl font-bold text-green-600">{plansData?.metriche?.enti_paganti || 0}</p>
                        <p className="text-xs text-green-700">Enti Paganti</p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-lg text-center">
                        <p className="text-xl font-bold text-amber-600">{plansData?.metriche?.upgrade_ultimi_30g || 0}</p>
                        <p className="text-xs text-amber-700">Upgrade (30g)</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Retention & Feature Usage */}
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Retention Metrics */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="w-5 h-5 text-[#0055BB]" />
                      Metriche Engagement
                    </CardTitle>
                    <CardDescription>Attività utenti negli ultimi periodi</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {retentionData && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-4 bg-blue-50 rounded-lg text-center">
                            <p className="text-2xl font-bold text-blue-600">{retentionData.attivi_7_giorni}</p>
                            <p className="text-xs text-blue-700">Attivi 7g</p>
                          </div>
                          <div className="p-4 bg-green-50 rounded-lg text-center">
                            <p className="text-2xl font-bold text-green-600">{retentionData.attivi_30_giorni}</p>
                            <p className="text-xs text-green-700">Attivi 30g</p>
                          </div>
                          <div className="p-4 bg-purple-50 rounded-lg text-center">
                            <p className="text-2xl font-bold text-purple-600">{retentionData.attivi_90_giorni}</p>
                            <p className="text-xs text-purple-700">Attivi 90g</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <UserX className="w-5 h-5 text-red-500" />
                            <span className="text-sm">Enti Dormienti</span>
                          </div>
                          <Badge className="bg-red-100 text-red-700">{retentionData.enti_dormienti}</Badge>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Ban className="w-5 h-5 text-slate-500" />
                            <span className="text-sm">Account Bloccati</span>
                          </div>
                          <Badge className="bg-slate-100 text-slate-700">{retentionData.enti_bloccati}</Badge>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Feature Usage */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[#0055BB]" />
                      Utilizzo Funzionalità
                    </CardTitle>
                    <CardDescription>{featureUsageData?.periodo || 'Ultimi 90 giorni'}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {featureUsageData?.feature_usage?.length > 0 ? (
                      <div className="space-y-3">
                        {featureUsageData.feature_usage.slice(0, 8).map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <span className="text-sm text-slate-600 w-28 truncate">{item.feature}</span>
                            <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                              <div 
                                className="bg-gradient-to-r from-[#0055BB] to-blue-400 h-full rounded-full flex items-center justify-end pr-2"
                                style={{ width: `${Math.max(item.percentuale, 5)}%` }}
                              >
                                <span className="text-xs text-white font-medium">{item.utilizzi}</span>
                              </div>
                            </div>
                            <span className="text-xs text-slate-400 w-12 text-right">{item.percentuale}%</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Info className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">Nessun dato di utilizzo disponibile</p>
                      </div>
                    )}
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-slate-500 text-center">
                        <span className="font-bold text-[#0055BB]">{featureUsageData?.totale_operazioni || 0}</span> operazioni totali registrate
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Volume Data */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#0055BB]" />
                    Volumi Dati Piattaforma
                  </CardTitle>
                  <CardDescription>Quantità totali di dati gestiti</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-600">{featureUsageData?.volumi_dati?.volontari || 0}</p>
                      <p className="text-sm text-blue-700">Volontari</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <Calendar className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-green-600">{featureUsageData?.volumi_dati?.eventi || 0}</p>
                      <p className="text-sm text-green-700">Eventi</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg text-center">
                      <CheckCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-amber-600">{featureUsageData?.volumi_dati?.presenze || 0}</p>
                      <p className="text-sm text-amber-700">Presenze</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <FileText className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-purple-600">{featureUsageData?.volumi_dati?.documenti || 0}</p>
                      <p className="text-sm text-purple-700">Documenti</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Refresh Button */}
              <div className="text-center">
                <Button onClick={fetchAnalytics} variant="outline" disabled={analyticsLoading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${analyticsLoading ? 'animate-spin' : ''}`} />
                  Aggiorna Analytics
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* TAB: RETENTION & RE-ENGAGEMENT */}
        <TabsContent value="retention" className="mt-6">
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-4">
              <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-80">Enti Dormienti</p>
                      <p className="text-3xl font-bold">{dormantEnti.length}</p>
                      <p className="text-xs opacity-70 mt-1">{`>${dormancyThreshold}g inattivi`}</p>
                    </div>
                    <UserX className="w-10 h-10 opacity-80" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-80">Tasso Dormienza</p>
                      <p className="text-3xl font-bold">{retentionStats?.tasso_dormienza || 0}%</p>
                      <p className="text-xs opacity-70 mt-1">del totale enti</p>
                    </div>
                    <AlertCircle className="w-10 h-10 opacity-80" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-80">Email Inviate</p>
                      <p className="text-3xl font-bold">{retentionStats?.notifiche?.totali || 0}</p>
                      <p className="text-xs opacity-70 mt-1">re-engagement totali</p>
                    </div>
                    <Mail className="w-10 h-10 opacity-80" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-80">Enti Contattati</p>
                      <p className="text-3xl font-bold">{retentionStats?.notifiche?.enti_contattati || 0}</p>
                      <p className="text-xs opacity-70 mt-1">almeno una notifica</p>
                    </div>
                    <Send className="w-10 h-10 opacity-80" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              {/* Dormant Enti List */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <UserX className="w-5 h-5 text-red-500" />
                        Enti Dormienti
                      </CardTitle>
                      <CardDescription>Enti senza attività recente</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500">Soglia:</Label>
                      <select 
                        className="text-sm border rounded px-2 py-1"
                        value={dormancyThreshold}
                        onChange={(e) => {
                          setDormancyThreshold(Number(e.target.value));
                          setTimeout(fetchRetentionData, 100);
                        }}
                      >
                        <option value={7}>7 giorni</option>
                        <option value={14}>14 giorni</option>
                        <option value={30}>30 giorni</option>
                        <option value={60}>60 giorni</option>
                        <option value={90}>90 giorni</option>
                      </select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {dormantEnti.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Nessun ente dormiente</p>
                      <p className="text-xs text-green-600">Ottimo! Tutti gli enti sono attivi</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {dormantEnti.slice(0, 20).map((ente, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-slate-800 text-sm">{ente.denominazione}</p>
                            <p className="text-xs text-slate-500">{ente.admin_email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className="bg-red-100 text-red-700 text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                {ente.giorni_inattivo}g inattivo
                              </Badge>
                              {ente.notifiche_inviate > 0 && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">
                                  {ente.notifiche_inviate} email
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="bg-blue-50 hover:bg-blue-100 text-blue-700"
                            onClick={() => sendReengagementEmail(ente.ente_id, ente.denominazione)}
                            disabled={sendingEmail}
                          >
                            <Send className="w-3 h-3 mr-1" />
                            Invia
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Bulk Action */}
                  {dormantEnti.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <Button 
                        onClick={sendBulkReengagement} 
                        disabled={sendingEmail}
                        className="w-full bg-gradient-to-r from-blue-500 to-blue-600"
                      >
                        {sendingEmail ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Invia a Tutti gli Enti Dormienti
                      </Button>
                      <p className="text-xs text-slate-500 text-center mt-2">
                        Max 50 email, cooldown 7 giorni tra invii
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Notification History */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-[#0055BB]" />
                    Storico Notifiche
                  </CardTitle>
                  <CardDescription>Ultime email di re-engagement inviate</CardDescription>
                </CardHeader>
                <CardContent>
                  {notificationHistory.length === 0 ? (
                    <div className="text-center py-8">
                      <Mail className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Nessuna notifica inviata</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {notificationHistory.map((notif, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-medium text-slate-800 text-sm">{notif.ente_nome}</p>
                            <Badge className={notif.type === 'bulk' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                              {notif.type === 'bulk' ? 'Bulk' : 'Manuale'}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500">{notif.to_email}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-slate-400">
                              {notif.sent_at ? new Date(notif.sent_at).toLocaleString('it-IT') : ''}
                            </span>
                            <span className="text-xs text-red-500">{notif.days_inactive}g inattivo</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Info Box */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Come funziona il Re-engagement</p>
                    <ul className="text-sm text-blue-700 mt-1 space-y-1">
                      <li>• Gli enti senza attività oltre la soglia vengono identificati come "dormienti"</li>
                      <li>• Puoi inviare email personalizzate per riportarli sulla piattaforma</li>
                      <li>• Il sistema rispetta un cooldown di 7 giorni tra le email allo stesso ente</li>
                      <li>• L'email include le ultime novità e un link diretto alla piattaforma</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Refresh */}
            <div className="text-center">
              <Button onClick={fetchRetentionData} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Aggiorna Dati
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* TAB: ELENCO ENTI */}
        <TabsContent value="enti" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#0055BB]" />
                    Elenco Enti Registrati
                  </CardTitle>
                  <CardDescription>Tutti gli enti registrati sulla piattaforma</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => exportData('csv')} variant="outline" disabled={exporting}>
                    <FileText className="w-4 h-4 mr-2" />
                    CSV
                  </Button>
                  <Button onClick={() => exportData('excel')} variant="outline" disabled={exporting}>
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Excel
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filtri */}
              <div className="grid gap-4 md:grid-cols-3 mb-6 p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label>Piano</Label>
                  <Select value={pianoFilter} onValueChange={setPianoFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Tutti i piani" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i piani</SelectItem>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="governance">Governance</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                      <SelectItem value="convenzioni">Convenzioni</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Regione</Label>
                  <Input 
                    placeholder="Filtra per regione..." 
                    value={regioneFilter}
                    onChange={(e) => setRegioneFilter(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Forma Giuridica</Label>
                  <Input 
                    placeholder="Es. APS, ODV..." 
                    value={naturaFilter}
                    onChange={(e) => setNaturaFilter(e.target.value)}
                  />
                </div>
              </div>

              {/* Tabella */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Denominazione</TableHead>
                      <TableHead>Forma Giuridica</TableHead>
                      <TableHead>Regione</TableHead>
                      <TableHead>Piano</TableHead>
                      <TableHead>Data Registrazione</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enti.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                          Nessun ente trovato
                        </TableCell>
                      </TableRow>
                    ) : (
                      enti.map((ente, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{ente.denominazione}</TableCell>
                          <TableCell>{ente.natura_giuridica}</TableCell>
                          <TableCell>{ente.regione}</TableCell>
                          <TableCell>
                            <Badge className={planColors[ente.piano] || planColors.free}>
                              {ente.piano?.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {ente.data_registrazione ? new Date(ente.data_registrazione).toLocaleDateString('it-IT') : 'N/D'}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[ente.stato_abbonamento] || statusColors.active}>
                              {ente.stato_abbonamento}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => fetchEnteDetail(ente.ente_id)}
                              data-testid={`manage-ente-${ente.ente_id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Dettagli
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: CONVENZIONI NAZIONALI */}
        <TabsContent value="convenzioni" className="mt-6 space-y-6">
          {convenzioniLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#0055BB]" />
            </div>
          ) : (
            <>
              {/* Header con stats e bottone */}
              <div className="flex flex-wrap justify-between items-center gap-4">
                <div className="flex gap-6">
                  <div className="bg-blue-50 px-4 py-3 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-600 font-medium">Convenzioni Attive</p>
                    <p className="text-2xl font-bold text-blue-800">
                      {convenzioniNazionali.filter(c => c.attiva).length}
                    </p>
                  </div>
                  <div className="bg-green-50 px-4 py-3 rounded-lg border border-green-200">
                    <p className="text-sm text-green-600 font-medium">Totale Enti Convenzionati</p>
                    <p className="text-2xl font-bold text-green-800">
                      {convenzioniNazionali.reduce((sum, c) => sum + (c.enti_aderenti || 0), 0)}
                    </p>
                  </div>
                  {convenzioniReport?.totale_generale && (
                    <div className="bg-purple-50 px-4 py-3 rounded-lg border border-purple-200">
                      <p className="text-sm text-purple-600 font-medium">Revenue Nazionale Stimato</p>
                      <p className="text-2xl font-bold text-purple-800">
                        €{convenzioniReport.totale_generale.incasso_nazionale_totale?.toLocaleString('it-IT') || '0'}
                      </p>
                    </div>
                  )}
                </div>
                <Button 
                  onClick={() => setShowNewConvenzioneModal(true)}
                  className="bg-[#0055BB] hover:bg-[#003d88]"
                  data-testid="add-convenzione-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuova Convenzione
                </Button>
              </div>

              {/* Lista Convenzioni */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Handshake className="w-5 h-5 text-[#0055BB]" />
                    Convenzioni Nazionali
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {convenzioniNazionali.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Handshake className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Nessuna convenzione definita</p>
                      <p className="text-sm">Crea la prima convenzione per federazioni o gruppi di enti</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {convenzioniNazionali.map((conv) => (
                        <div 
                          key={conv.convenzione_id}
                          className={`border rounded-lg p-4 ${!conv.attiva ? 'bg-gray-50 opacity-70' : 'bg-white'}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <h3 className="font-semibold text-lg">{conv.nome}</h3>
                                {conv.attiva ? (
                                  <Badge className="bg-green-100 text-green-800">Attiva</Badge>
                                ) : (
                                  <Badge variant="secondary">Disattivata</Badge>
                                )}
                                {conv.enti_aderenti > 0 && (
                                  <Badge variant="outline">{conv.enti_aderenti} enti</Badge>
                                )}
                              </div>
                              {conv.descrizione && (
                                <p className="text-sm text-gray-600 mt-1">{conv.descrizione}</p>
                              )}
                              
                              {/* CODICE CONVENZIONE */}
                              {conv.codice_convenzione && (
                                <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 inline-flex items-center gap-3">
                                  <div>
                                    <p className="text-xs text-blue-600 font-medium">Codice Convenzione</p>
                                    <p className="text-lg font-mono font-bold text-blue-800">{conv.codice_convenzione}</p>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                                    onClick={() => {
                                      navigator.clipboard.writeText(conv.codice_convenzione);
                                      toast.success('Codice copiato negli appunti!');
                                    }}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  </Button>
                                </div>
                              )}
                              
                              <div className="flex flex-wrap gap-4 mt-3 text-sm">
                                <span className="flex items-center gap-1 text-gray-600">
                                  <Percent className="w-4 h-4" />
                                  {conv.tipo_sconto === 'percentuale' 
                                    ? `Sconto ${conv.sconto_percentuale}%`
                                    : `Prezzo fisso €${conv.prezzo_fisso}`
                                  }
                                </span>
                                {conv.revenue_sharing && (
                                  <span className="flex items-center gap-1 text-purple-600">
                                    <TrendingUp className="w-4 h-4" />
                                    Revenue sharing: {conv.revenue_sharing}%
                                  </span>
                                )}
                                {conv.fee_nazionale && (
                                  <span className="flex items-center gap-1 text-orange-600">
                                    <DollarSign className="w-4 h-4" />
                                    Fee: €{conv.fee_nazionale}/ente
                                  </span>
                                )}
                                {conv.data_scadenza && (
                                  <span className="flex items-center gap-1 text-gray-500">
                                    <Calendar className="w-4 h-4" />
                                    Scade: {conv.data_scadenza}
                                  </span>
                                )}
                                {conv.max_enti && (
                                  <span className="text-gray-500">
                                    Max: {conv.max_enti} enti
                                  </span>
                                )}
                              </div>
                              {(conv.revenue_stimato > 0 || conv.fee_nazionale_stimato > 0) && (
                                <div className="mt-3 pt-3 border-t flex gap-6 text-sm">
                                  <span className="text-green-700">
                                    Revenue stimato: <strong>€{conv.revenue_stimato?.toLocaleString('it-IT')}</strong>
                                  </span>
                                  <span className="text-orange-700">
                                    Fee stimato: <strong>€{conv.fee_nazionale_stimato?.toLocaleString('it-IT')}</strong>
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 ml-4">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => fetchConvenzioneEnti(conv.convenzione_id)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Vedi Enti
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => deleteConvenzione(conv.convenzione_id, conv.nome)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Report Riepilogativo */}
              {convenzioniReport && convenzioniReport.convenzioni?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-purple-600" />
                      Report Revenue Convenzioni - {convenzioniReport.anno}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Convenzione</TableHead>
                          <TableHead className="text-center">Enti</TableHead>
                          <TableHead className="text-right">Revenue Sharing</TableHead>
                          <TableHead className="text-right">Fee Nazionale</TableHead>
                          <TableHead className="text-right">Totale</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {convenzioniReport.convenzioni.map((r) => (
                          <TableRow key={r.convenzione_id}>
                            <TableCell className="font-medium">{r.nome}</TableCell>
                            <TableCell className="text-center">{r.enti_aderenti}</TableCell>
                            <TableCell className="text-right">€{r.revenue_sharing?.toLocaleString('it-IT')}</TableCell>
                            <TableCell className="text-right">€{r.fee_nazionale?.toLocaleString('it-IT')}</TableCell>
                            <TableCell className="text-right font-bold text-green-700">
                              €{r.incasso_totale?.toLocaleString('it-IT')}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-gray-50 font-bold">
                          <TableCell>TOTALE</TableCell>
                          <TableCell className="text-center">{convenzioniReport.totale_generale.totale_enti}</TableCell>
                          <TableCell className="text-right">€{convenzioniReport.totale_generale.totale_revenue_sharing?.toLocaleString('it-IT')}</TableCell>
                          <TableCell className="text-right">€{convenzioniReport.totale_generale.totale_fee_nazionale?.toLocaleString('it-IT')}</TableCell>
                          <TableCell className="text-right text-green-700">€{convenzioniReport.totale_generale.incasso_nazionale_totale?.toLocaleString('it-IT')}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* TAB: GESTIONE ENTI */}
        <TabsContent value="gestione" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Storico Azioni Admin */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-[#0055BB]" />
                  Storico Azioni Admin
                </CardTitle>
                <CardDescription>Ultime modifiche effettuate dagli amministratori</CardDescription>
              </CardHeader>
              <CardContent>
                {adminLogs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Info className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Nessuna azione registrata</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {adminLogs.map((log, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center justify-between mb-1">
                          <Badge className={
                            log.azione === 'cambio_piano' ? 'bg-amber-100 text-amber-700' :
                            log.azione === 'blocco_ente' ? 'bg-red-100 text-red-700' :
                            log.azione === 'sblocco_ente' ? 'bg-green-100 text-green-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {log.azione?.replace(/_/g, ' ').toUpperCase()}
                          </Badge>
                          <span className="text-xs text-slate-400">
                            {log.timestamp ? new Date(log.timestamp).toLocaleString('it-IT') : ''}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-slate-700">{log.ente_nome}</p>
                        {log.old_value && log.new_value && (
                          <p className="text-xs text-slate-500">
                            {log.old_value.toUpperCase()} → {log.new_value.toUpperCase()}
                          </p>
                        )}
                        {log.motivo && (
                          <p className="text-xs text-slate-500 italic mt-1">"{log.motivo}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Azioni Rapide */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#0055BB]" />
                  Gestione Rapida
                </CardTitle>
                <CardDescription>Seleziona un ente dalla lista per gestirlo</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-800 mb-2">Come usare la gestione enti</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>1. Vai alla tab "Enti" o cerca un ente specifico</li>
                      <li>2. Clicca su "Dettagli" per aprire il pannello gestione</li>
                      <li>3. Da lì puoi modificare il piano o bloccare/sbloccare l'account</li>
                    </ul>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 bg-amber-50 rounded-lg text-center">
                      <Crown className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-amber-800">Upgrade Piano</p>
                      <p className="text-xs text-amber-600">Regala Premium o Pro</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg text-center">
                      <Ban className="w-8 h-8 text-red-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-red-800">Blocca Account</p>
                      <p className="text-xs text-red-600">Sospendi accesso utente</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB: MONITORAGGIO PIANI */}
        <TabsContent value="piani" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* SEZIONE 3: Monitoraggio Piani */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-[#0055BB]" />
                  Distribuzione Piani
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 bg-slate-400 rounded"></div>
                      <span>Piano Free</span>
                    </div>
                    <span className="text-2xl font-bold">{stats?.piani?.free || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 bg-amber-400 rounded"></div>
                      <span>Piano Premium</span>
                    </div>
                    <span className="text-2xl font-bold text-amber-600">{stats?.piani?.premium || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 bg-purple-400 rounded"></div>
                      <span>Piano Pro</span>
                    </div>
                    <span className="text-2xl font-bold text-purple-600">{stats?.piani?.pro || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stato Abbonamenti */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#0055BB]" />
                  Stato Abbonamenti
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <span>Abbonamenti Scaduti</span>
                    </div>
                    <span className="text-2xl font-bold text-red-600">{stats?.piani?.scaduti || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-blue-500" />
                      <span>In Prova Gratuita</span>
                    </div>
                    <span className="text-2xl font-bold text-blue-600">{stats?.piani?.in_prova || 0}</span>
                  </div>
                </div>
                
                {/* SEZIONE 6: Monitoraggio Abbonamenti */}
                {abbonamenti && (
                  <div className="mt-6 pt-6 border-t">
                    <h4 className="font-medium mb-4">Riepilogo Finanziario</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-600">Abbonamenti Attivi</p>
                        <p className="text-2xl font-bold text-green-700">{abbonamenti.abbonamenti_attivi?.totale || 0}</p>
                      </div>
                      <div className="text-center p-4 bg-emerald-50 rounded-lg">
                        <p className="text-sm text-emerald-600">Entrate Mensili Stimate</p>
                        <p className="text-2xl font-bold text-emerald-700">€{abbonamenti.entrate_mensili_stimate || 0}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-3 text-center">{abbonamenti.nota}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB: UTILIZZO PIATTAFORMA */}
        <TabsContent value="utilizzo" className="mt-6">
          {/* SEZIONE 5: Utilizzo Piattaforma */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Volontari Totali</p>
                    <p className="text-2xl font-bold">{stats?.utilizzo?.totale_volontari || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <FileText className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Documenti Caricati</p>
                    <p className="text-2xl font-bold">{stats?.utilizzo?.totale_documenti || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Calendar className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Eventi Creati</p>
                    <p className="text-2xl font-bold">{stats?.utilizzo?.totale_eventi || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-100 rounded-lg">
                    <Activity className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Attività Registrate</p>
                    <p className="text-2xl font-bold">{stats?.utilizzo?.totale_attivita || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Statistiche Aggregate Piattaforma</CardTitle>
              <CardDescription>Dati di utilizzo complessivi di tutti gli enti</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <h4 className="font-medium text-slate-700">Engagement</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Media volontari per ente</span>
                      <span className="font-medium">
                        {stats?.contatori?.totale_enti > 0 
                          ? Math.round(stats.utilizzo.totale_volontari / stats.contatori.totale_enti) 
                          : 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Media eventi per ente</span>
                      <span className="font-medium">
                        {stats?.contatori?.totale_enti > 0 
                          ? Math.round(stats.utilizzo.totale_eventi / stats.contatori.totale_enti * 10) / 10
                          : 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Media documenti per ente</span>
                      <span className="font-medium">
                        {stats?.contatori?.totale_enti > 0 
                          ? Math.round(stats.utilizzo.totale_documenti / stats.contatori.totale_enti * 10) / 10
                          : 0}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <h4 className="font-medium text-slate-700">Conversione</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Tasso conversione Premium</span>
                      <span className="font-medium text-amber-600">
                        {stats?.contatori?.totale_enti > 0 
                          ? Math.round((stats.piani.premium / stats.contatori.totale_enti) * 100)
                          : 0}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Tasso conversione Pro</span>
                      <span className="font-medium text-purple-600">
                        {stats?.contatori?.totale_enti > 0 
                          ? Math.round((stats.piani.pro / stats.contatori.totale_enti) * 100)
                          : 0}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Totale paganti</span>
                      <span className="font-medium text-green-600">
                        {(stats?.piani?.premium || 0) + (stats?.piani?.pro || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: STORAGE MONITORING */}
        <TabsContent value="storage" className="mt-6">
          {storageLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#0055BB]" />
            </div>
          ) : storageData ? (
            <div className="space-y-6">
              {/* Storage Overview Cards */}
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <HardDrive className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Storage Totale Usato</p>
                        <p className="text-2xl font-bold">{storageData.storage_totale_readable}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-green-100 rounded-lg">
                        <Database className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Enti con Storage</p>
                        <p className="text-2xl font-bold">{storageData.totale_enti_con_storage}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className={storageData.enti_vicini_limite > 0 ? 'border-amber-300' : ''}>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-amber-100 rounded-lg">
                        <AlertTriangle className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Vicini al Limite (≥80%)</p>
                        <p className="text-2xl font-bold text-amber-600">{storageData.enti_vicini_limite}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className={storageData.enti_oltre_limite > 0 ? 'border-red-300' : ''}>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-red-100 rounded-lg">
                        <AlertCircle className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Oltre Limite</p>
                        <p className="text-2xl font-bold text-red-600">{storageData.enti_oltre_limite}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Storage Table */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <HardDrive className="w-5 h-5 text-[#0055BB]" />
                        Utilizzo Storage per Ente
                      </CardTitle>
                      <CardDescription>Top enti per utilizzo storage</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchStorageData}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Aggiorna
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {storageData.enti_storage?.length === 0 ? (
                    <div className="text-center py-8">
                      <HardDrive className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">Nessun ente ha ancora caricato documenti</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ente</TableHead>
                          <TableHead>Piano</TableHead>
                          <TableHead>Usato</TableHead>
                          <TableHead>Limite</TableHead>
                          <TableHead>Utilizzo</TableHead>
                          <TableHead>Stato</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {storageData.enti_storage.map((ente) => (
                          <TableRow key={ente.ente_id}>
                            <TableCell className="font-medium">{ente.denominazione}</TableCell>
                            <TableCell>
                              <Badge className={planColors[ente.piano] || planColors.free}>
                                {ente.piano?.toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell>{ente.usato_readable}</TableCell>
                            <TableCell>{ente.limite_readable}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full ${
                                      ente.percentuale >= 100 ? 'bg-red-500' :
                                      ente.percentuale >= 80 ? 'bg-amber-500' :
                                      ente.percentuale >= 50 ? 'bg-blue-500' :
                                      'bg-green-500'
                                    }`}
                                    style={{ width: `${Math.min(ente.percentuale, 100)}%` }}
                                  />
                                </div>
                                <span className="text-sm text-slate-600">{ente.percentuale}%</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={
                                ente.status === 'over_limit' ? 'bg-red-100 text-red-700' :
                                ente.status === 'near_limit' ? 'bg-amber-100 text-amber-700' :
                                ente.status === 'moderate' ? 'bg-blue-100 text-blue-700' :
                                'bg-green-100 text-green-700'
                              }>
                                {ente.status === 'over_limit' ? 'Oltre limite' :
                                 ente.status === 'near_limit' ? 'Quasi pieno' :
                                 ente.status === 'moderate' ? 'Moderato' : 'OK'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-12">
              <HardDrive className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Clicca sul tab per caricare i dati storage</p>
            </div>
          )}
        </TabsContent>

        {/* TAB: NOTIFICHE AMMINISTRATORE */}
        <TabsContent value="notifiche" className="mt-6">
          {/* SEZIONE 7: Notifiche Amministratore */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#0055BB]" />
                Notifiche Recenti
              </CardTitle>
              <CardDescription>Ultime attività sulla piattaforma (7 giorni)</CardDescription>
            </CardHeader>
            <CardContent>
              {notifiche.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <p className="text-slate-500">Nessuna notifica recente</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifiche.map((notifica, idx) => (
                    <div 
                      key={idx} 
                      className={`p-4 rounded-lg border flex items-start gap-4 ${
                        notifica.priorita === 'warning' ? 'bg-amber-50 border-amber-200' :
                        notifica.priorita === 'success' ? 'bg-green-50 border-green-200' :
                        'bg-blue-50 border-blue-200'
                      }`}
                    >
                      {getNotificaIcon(notifica.tipo)}
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{notifica.titolo}</p>
                        <p className="text-sm text-slate-600">{notifica.messaggio}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {notifica.data ? new Date(notifica.data).toLocaleString('it-IT') : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Dettagli Ente */}
      {showEnteModal && enteDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">
                    {enteDetail.ente?.denominazione || 'Dettagli Ente'}
                  </h2>
                  <p className="text-sm text-slate-500">ID: {enteDetail.ente?.ente_id}</p>
                </div>
                <Button variant="ghost" onClick={() => setShowEnteModal(false)}>
                  ✕
                </Button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Info Abbonamento */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Abbonamento Attuale
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Piano</p>
                    <Badge className={planColors[enteDetail.subscription?.plan] || planColors.free}>
                      {enteDetail.subscription?.plan?.toUpperCase() || 'FREE'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Stato</p>
                    <Badge className={statusColors[enteDetail.subscription?.status] || statusColors.active}>
                      {enteDetail.subscription?.status || 'active'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Statistiche Utilizzo */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Utilizzo
                </h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{enteDetail.stats?.volontari || 0}</p>
                    <p className="text-xs text-slate-500">Volontari</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{enteDetail.stats?.eventi || 0}</p>
                    <p className="text-xs text-slate-500">Eventi</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{enteDetail.stats?.documenti || 0}</p>
                    <p className="text-xs text-slate-500">Documenti</p>
                  </div>
                </div>
              </div>

              {/* Sezione Soci */}
              <div className="p-4 bg-purple-50 rounded-lg">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-600" />
                  Soci e Quote {enteDetail.soci_stats?.quote_anno || new Date().getFullYear()}
                </h3>
                <div className="grid grid-cols-3 gap-4 text-center mb-4">
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{enteDetail.soci_stats?.totali || 0}</p>
                    <p className="text-xs text-slate-500">Totali</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{enteDetail.soci_stats?.attivi || 0}</p>
                    <p className="text-xs text-slate-500">Attivi</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-500">{enteDetail.soci_stats?.cessati || 0}</p>
                    <p className="text-xs text-slate-500">Cessati</p>
                  </div>
                </div>
                <div className="border-t border-purple-200 pt-3">
                  <div className="flex justify-between items-center text-sm mb-2">
                    <span className="text-slate-600">Quote emesse:</span>
                    <span className="font-medium">{enteDetail.soci_stats?.quote_totali || 0}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm mb-2">
                    <span className="text-slate-600">Quote pagate:</span>
                    <span className="font-medium text-green-600">{enteDetail.soci_stats?.quote_pagate || 0}</span>
                  </div>
                  {(enteDetail.soci_stats?.importo_da_incassare || 0) > 0 && (
                    <div className="flex justify-between items-center text-sm mt-2 pt-2 border-t border-purple-200">
                      <span className="text-slate-600">Da incassare:</span>
                      <span className="font-bold text-amber-600">€{enteDetail.soci_stats?.importo_da_incassare?.toFixed(2) || '0.00'}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Utenti */}
              <div className="p-4 bg-green-50 rounded-lg">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Utenti ({enteDetail.users?.length || 0})
                </h3>
                <div className="space-y-2">
                  {enteDetail.users?.map((user, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span>{user.name} ({user.email})</span>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-slate-100 text-slate-600 text-xs">{user.role}</Badge>
                        {user.blocked && <Badge className="bg-red-100 text-red-600 text-xs">BLOCCATO</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Azioni Admin */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Azioni Amministratore
                </h3>
                
                {/* Cambio Piano */}
                <div className="mb-4">
                  <Label className="text-sm mb-2 block">Cambia Piano</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant={enteDetail.subscription?.plan === 'free' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => changePlan(enteDetail.ente?.ente_id, 'free', 'Downgrade manuale')}
                      disabled={actionLoading || enteDetail.subscription?.plan === 'free'}
                    >
                      Free
                    </Button>
                    <Button 
                      variant={enteDetail.subscription?.plan === 'premium' ? 'default' : 'outline'}
                      size="sm"
                      className={enteDetail.subscription?.plan !== 'premium' ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
                      onClick={() => changePlan(enteDetail.ente?.ente_id, 'premium', 'Upgrade manuale admin')}
                      disabled={actionLoading || enteDetail.subscription?.plan === 'premium'}
                    >
                      <Crown className="w-3 h-3 mr-1" />
                      Premium
                    </Button>
                    <Button 
                      variant={enteDetail.subscription?.plan === 'governance' ? 'default' : 'outline'}
                      size="sm"
                      className={enteDetail.subscription?.plan !== 'governance' ? 'bg-indigo-500 hover:bg-indigo-600 text-white' : ''}
                      onClick={() => changePlan(enteDetail.ente?.ente_id, 'governance', 'Upgrade manuale admin')}
                      disabled={actionLoading || enteDetail.subscription?.plan === 'governance'}
                    >
                      <Building2 className="w-3 h-3 mr-1" />
                      Governance
                    </Button>
                    <Button 
                      variant={enteDetail.subscription?.plan === 'pro' ? 'default' : 'outline'}
                      size="sm"
                      className={enteDetail.subscription?.plan !== 'pro' ? 'bg-purple-500 hover:bg-purple-600 text-white' : ''}
                      onClick={() => changePlan(enteDetail.ente?.ente_id, 'pro', 'Upgrade manuale admin')}
                      disabled={actionLoading || enteDetail.subscription?.plan === 'pro'}
                    >
                      <Zap className="w-3 h-3 mr-1" />
                      Pro
                    </Button>
                    <Button 
                      variant={enteDetail.subscription?.plan === 'enterprise' ? 'default' : 'outline'}
                      size="sm"
                      className={enteDetail.subscription?.plan !== 'enterprise' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : ''}
                      onClick={() => changePlan(enteDetail.ente?.ente_id, 'enterprise', 'Upgrade manuale admin')}
                      disabled={actionLoading || enteDetail.subscription?.plan === 'enterprise'}
                    >
                      <Target className="w-3 h-3 mr-1" />
                      Enterprise
                    </Button>
                    <Button 
                      variant={enteDetail.subscription?.plan === 'convenzioni' ? 'default' : 'outline'}
                      size="sm"
                      className={enteDetail.subscription?.plan !== 'convenzioni' ? 'bg-rose-500 hover:bg-rose-600 text-white' : ''}
                      onClick={() => changePlan(enteDetail.ente?.ente_id, 'convenzioni', 'Upgrade Convenzione Nazionale')}
                      disabled={actionLoading || enteDetail.subscription?.plan === 'convenzioni'}
                    >
                      <Users className="w-3 h-3 mr-1" />
                      Convenzioni
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Prezzi (IVA incl.): Free €0 | Premium €9,99 | Governance €14,99 | Pro €24,99 | Enterprise €49,99 | Convenzioni: Custom
                  </p>
                </div>

                {/* Blocco/Sblocco */}
                <div>
                  <Label className="text-sm mb-2 block">Stato Account</Label>
                  {enteDetail.ente?.blocked ? (
                    <Button 
                      variant="outline"
                      size="sm"
                      className="bg-green-500 hover:bg-green-600 text-white"
                      onClick={() => toggleBlockEnte(enteDetail.ente?.ente_id, false, 'Sblocco manuale admin')}
                      disabled={actionLoading}
                    >
                      <Unlock className="w-4 h-4 mr-2" />
                      Sblocca Account
                    </Button>
                  ) : (
                    <Button 
                      variant="outline"
                      size="sm"
                      className="bg-red-500 hover:bg-red-600 text-white"
                      onClick={() => toggleBlockEnte(enteDetail.ente?.ente_id, true, 'Blocco manuale admin')}
                      disabled={actionLoading}
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Blocca Account
                    </Button>
                  )}
                </div>
              </div>

              {/* Sezione Convenzione/Prezzo Personalizzato */}
              <div className="p-4 bg-rose-50 rounded-lg border border-rose-200 mt-4">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-rose-600" />
                  Prezzo Personalizzato / Convenzione
                </h3>
                
                <div className="space-y-4">
                  {/* Toggle Convenzione */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="has_convenzione"
                      checked={convenzioneForm.has_convenzione}
                      onChange={(e) => setConvenzioneForm({...convenzioneForm, has_convenzione: e.target.checked})}
                      className="w-4 h-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500"
                    />
                    <Label htmlFor="has_convenzione" className="text-sm font-medium">
                      Applica prezzo speciale / convenzione
                    </Label>
                  </div>
                  
                  {convenzioneForm.has_convenzione && (
                    <>
                      {/* Nome Convenzione */}
                      <div>
                        <Label className="text-xs mb-1 block">Nome Convenzione</Label>
                        <Input
                          placeholder="es. ANPAS Sardegna, CSV Regionale..."
                          value={convenzioneForm.convenzione_nome}
                          onChange={(e) => setConvenzioneForm({...convenzioneForm, convenzione_nome: e.target.value})}
                          className="h-8 text-sm"
                        />
                      </div>
                      
                      {/* Tipo Sconto */}
                      <div>
                        <Label className="text-xs mb-1 block">Tipo Sconto</Label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="radio"
                              name="tipo_sconto"
                              value="percentuale"
                              checked={convenzioneForm.tipo_sconto === 'percentuale'}
                              onChange={(e) => setConvenzioneForm({...convenzioneForm, tipo_sconto: e.target.value})}
                              className="text-rose-600 focus:ring-rose-500"
                            />
                            Sconto %
                          </label>
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="radio"
                              name="tipo_sconto"
                              value="fisso"
                              checked={convenzioneForm.tipo_sconto === 'fisso'}
                              onChange={(e) => setConvenzioneForm({...convenzioneForm, tipo_sconto: e.target.value})}
                              className="text-rose-600 focus:ring-rose-500"
                            />
                            Prezzo Fisso €
                          </label>
                        </div>
                      </div>
                      
                      {/* Sconto Percentuale o Prezzo Fisso */}
                      {convenzioneForm.tipo_sconto === 'percentuale' ? (
                        <div>
                          <Label className="text-xs mb-1 block">Sconto Percentuale</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              placeholder="30"
                              min="0"
                              max="100"
                              value={convenzioneForm.sconto_percentuale}
                              onChange={(e) => setConvenzioneForm({...convenzioneForm, sconto_percentuale: e.target.value})}
                              className="h-8 text-sm w-24"
                            />
                            <span className="text-sm text-gray-500">%</span>
                            {convenzioneForm.sconto_percentuale && (
                              <span className="text-xs text-rose-600 ml-2">
                                (es. Pro €24,99 → €{(24.99 * (1 - convenzioneForm.sconto_percentuale/100)).toFixed(2)}/mese)
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Label className="text-xs mb-1 block">Prezzo Fisso</Label>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">€</span>
                            <Input
                              type="number"
                              placeholder="99.00"
                              min="0"
                              step="0.01"
                              value={convenzioneForm.prezzo_fisso}
                              onChange={(e) => setConvenzioneForm({...convenzioneForm, prezzo_fisso: e.target.value})}
                              className="h-8 text-sm w-28"
                            />
                            <span className="text-sm text-gray-500">/anno</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Data Scadenza */}
                      <div>
                        <Label className="text-xs mb-1 block">Scadenza Convenzione</Label>
                        <Input
                          type="date"
                          value={convenzioneForm.convenzione_scadenza}
                          onChange={(e) => setConvenzioneForm({...convenzioneForm, convenzione_scadenza: e.target.value})}
                          className="h-8 text-sm w-40"
                        />
                      </div>
                      
                      {/* Note */}
                      <div>
                        <Label className="text-xs mb-1 block">Note (opzionale)</Label>
                        <Input
                          placeholder="Note interne sulla convenzione..."
                          value={convenzioneForm.convenzione_note}
                          onChange={(e) => setConvenzioneForm({...convenzioneForm, convenzione_note: e.target.value})}
                          className="h-8 text-sm"
                        />
                      </div>
                    </>
                  )}
                  
                  {/* Bottone Salva */}
                  <Button
                    onClick={() => saveConvenzione(enteDetail.ente?.ente_id)}
                    disabled={savingConvenzione}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    size="sm"
                  >
                    {savingConvenzione ? 'Salvataggio...' : 'Salva Impostazioni Convenzione'}
                  </Button>
                  
                  {/* Info attuale */}
                  {enteDetail.ente?.has_convenzione && (
                    <div className="mt-3 p-2 bg-white rounded border border-rose-200 text-xs">
                      <p className="font-medium text-rose-700">Convenzione attiva:</p>
                      <p>• {enteDetail.ente.convenzione_nome || 'Senza nome'}</p>
                      <p>• {enteDetail.ente.tipo_sconto === 'fisso' 
                          ? `Prezzo fisso: €${enteDetail.ente.prezzo_fisso}/anno` 
                          : `Sconto: ${enteDetail.ente.sconto_percentuale}%`}</p>
                      {enteDetail.ente.convenzione_scadenza && (
                        <p>• Scade: {enteDetail.ente.convenzione_scadenza}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Sezione Note Admin */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mt-4">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-amber-600" />
                  Note Admin (uso interno)
                </h3>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="Inserisci note interne sull'ente (es. contatti, richieste speciali, problemi segnalati...)"
                  className="w-full h-24 p-3 text-sm border border-amber-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-amber-600">
                    {enteDetail.ente?.admin_note_updated_at && (
                      <>Ultima modifica: {new Date(enteDetail.ente.admin_note_updated_at).toLocaleString('it-IT')}</>
                    )}
                  </p>
                  <Button
                    onClick={() => saveAdminNote(enteDetail.ente?.ente_id)}
                    disabled={savingNote}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    size="sm"
                  >
                    {savingNote ? 'Salvataggio...' : 'Salva Nota'}
                  </Button>
                </div>
              </div>

              {/* Sezione Storico Pagamenti/Movimenti */}
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 mt-4">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-emerald-600" />
                  Storico Movimenti Economici
                </h3>
                
                {loadingPayments ? (
                  <p className="text-sm text-gray-500">Caricamento...</p>
                ) : storicoPayments.length === 0 ? (
                  <p className="text-sm text-gray-500">Nessun movimento registrato</p>
                ) : (
                  <>
                    {/* Tabella movimenti */}
                    <div className="max-h-48 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-emerald-100 sticky top-0">
                          <tr>
                            <th className="text-left p-2">Data</th>
                            <th className="text-left p-2">Tipo</th>
                            <th className="text-left p-2">Descrizione</th>
                            <th className="text-right p-2">Importo</th>
                            <th className="text-left p-2">Metodo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {storicoPayments.slice(0, 20).map((payment, idx) => (
                            <tr key={idx} className="border-b border-emerald-100 hover:bg-emerald-50">
                              <td className="p-2">
                                {payment.data ? new Date(payment.data).toLocaleDateString('it-IT') : '-'}
                              </td>
                              <td className="p-2">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${
                                  payment.tipo === 'quota_socio' ? 'bg-blue-100 text-blue-700' :
                                  payment.tipo === 'abbonamento' ? 'bg-purple-100 text-purple-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {payment.tipo === 'quota_socio' ? 'Quota' : 
                                   payment.tipo === 'abbonamento' ? 'Abb.' : 'Entrata'}
                                </span>
                              </td>
                              <td className="p-2 max-w-32 truncate" title={payment.descrizione}>
                                {payment.descrizione}
                              </td>
                              <td className="p-2 text-right font-medium text-emerald-700">
                                {payment.importo != null ? `€${payment.importo.toFixed(2)}` : '-'}
                              </td>
                              <td className="p-2 capitalize">{payment.metodo || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Totali */}
                    {storicoPayments.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-emerald-200 grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-xs text-gray-500">Quote Soci</p>
                          <p className="font-bold text-emerald-700">
                            €{storicoPayments.filter(p => p.tipo === 'quota_socio').reduce((sum, p) => sum + (p.importo || 0), 0).toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Altre Entrate</p>
                          <p className="font-bold text-emerald-700">
                            €{storicoPayments.filter(p => p.tipo === 'entrata').reduce((sum, p) => sum + (p.importo || 0), 0).toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Totale</p>
                          <p className="font-bold text-emerald-800">
                            €{storicoPayments.reduce((sum, p) => sum + (p.importo || 0), 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
                
              {/* Delete Ente Section */}
              <div className="mt-6 pt-4 border-t border-red-200">
                <h5 className="text-sm font-medium text-red-700 mb-3">Zona Pericolosa</h5>
                <Button 
                  variant="outline"
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white border-red-600"
                  onClick={() => handleDeleteEnte(enteDetail.ente?.ente_id, enteDetail.ente?.denominazione)}
                  disabled={actionLoading}
                  data-testid="delete-ente-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina Ente Definitivamente
                </Button>
                <p className="text-xs text-red-500 mt-2">
                  Questa azione eliminerà l'ente e TUTTI i dati associati (volontari, soci, eventi, documenti, ecc.). L'operazione è irreversibile.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============= MODALE: NUOVA CONVENZIONE NAZIONALE ============= */}
      {showNewConvenzioneModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Handshake className="w-5 h-5 text-[#0055BB]" />
                Nuova Convenzione Nazionale
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowNewConvenzioneModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Nome Convenzione *</label>
                  <Input
                    value={newConvenzioneForm.nome}
                    onChange={(e) => setNewConvenzioneForm({...newConvenzioneForm, nome: e.target.value})}
                    placeholder="Es. Federazione Nazionale XYZ"
                    data-testid="convenzione-nome"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Descrizione</label>
                  <textarea
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    rows={2}
                    value={newConvenzioneForm.descrizione}
                    onChange={(e) => setNewConvenzioneForm({...newConvenzioneForm, descrizione: e.target.value})}
                    placeholder="Descrizione opzionale della convenzione..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo Sconto</label>
                  <Select 
                    value={newConvenzioneForm.tipo_sconto}
                    onValueChange={(v) => setNewConvenzioneForm({...newConvenzioneForm, tipo_sconto: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentuale">Sconto Percentuale</SelectItem>
                      <SelectItem value="fisso">Prezzo Fisso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newConvenzioneForm.tipo_sconto === 'percentuale' ? (
                  <div>
                    <label className="block text-sm font-medium mb-1">Sconto (%)</label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={newConvenzioneForm.sconto_percentuale}
                      onChange={(e) => setNewConvenzioneForm({...newConvenzioneForm, sconto_percentuale: e.target.value})}
                      placeholder="Es. 20"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium mb-1">Prezzo Fisso (€/anno)</label>
                    <Input
                      type="number"
                      min="0"
                      value={newConvenzioneForm.prezzo_fisso}
                      onChange={(e) => setNewConvenzioneForm({...newConvenzioneForm, prezzo_fisso: e.target.value})}
                      placeholder="Es. 150"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1">Revenue Sharing (%)</label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={newConvenzioneForm.revenue_sharing}
                    onChange={(e) => setNewConvenzioneForm({...newConvenzioneForm, revenue_sharing: e.target.value})}
                    placeholder="% al Nazionale"
                  />
                  <p className="text-xs text-gray-500 mt-1">Percentuale del canone che va al nazionale</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fee Nazionale (€/ente)</label>
                  <Input
                    type="number"
                    min="0"
                    value={newConvenzioneForm.fee_nazionale}
                    onChange={(e) => setNewConvenzioneForm({...newConvenzioneForm, fee_nazionale: e.target.value})}
                    placeholder="Fee fissa per ente"
                  />
                  <p className="text-xs text-gray-500 mt-1">Quota fissa per ogni ente aderente</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Data Inizio</label>
                  <Input
                    type="date"
                    value={newConvenzioneForm.data_inizio}
                    onChange={(e) => setNewConvenzioneForm({...newConvenzioneForm, data_inizio: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Data Scadenza</label>
                  <Input
                    type="date"
                    value={newConvenzioneForm.data_scadenza}
                    onChange={(e) => setNewConvenzioneForm({...newConvenzioneForm, data_scadenza: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max Enti (opzionale)</label>
                  <Input
                    type="number"
                    min="1"
                    value={newConvenzioneForm.max_enti}
                    onChange={(e) => setNewConvenzioneForm({...newConvenzioneForm, max_enti: e.target.value})}
                    placeholder="Nessun limite"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="conv-attiva"
                    checked={newConvenzioneForm.attiva}
                    onChange={(e) => setNewConvenzioneForm({...newConvenzioneForm, attiva: e.target.checked})}
                  />
                  <label htmlFor="conv-attiva" className="text-sm">Attiva immediatamente</label>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Note Interne</label>
                  <textarea
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    rows={2}
                    value={newConvenzioneForm.note}
                    onChange={(e) => setNewConvenzioneForm({...newConvenzioneForm, note: e.target.value})}
                    placeholder="Note interne sulla convenzione..."
                  />
                </div>
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowNewConvenzioneModal(false)}>
                Annulla
              </Button>
              <Button 
                onClick={createConvenzioneNazionale}
                disabled={savingNewConvenzione || !newConvenzioneForm.nome}
                className="bg-[#0055BB] hover:bg-[#003d88]"
              >
                {savingNewConvenzione ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creazione...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Crea Convenzione
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ============= MODALE: ENTI DELLA CONVENZIONE ============= */}
      {showConvenzioneEntiModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#0055BB]" />
                  Enti - {showConvenzioneEntiModal.nome}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {showConvenzioneEntiModal.tipo_sconto === 'percentuale' 
                    ? `Sconto ${showConvenzioneEntiModal.sconto_percentuale}%` 
                    : `Prezzo fisso €${showConvenzioneEntiModal.prezzo_fisso}`
                  }
                  {showConvenzioneEntiModal.revenue_sharing && ` • Revenue sharing ${showConvenzioneEntiModal.revenue_sharing}%`}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowConvenzioneEntiModal(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-6">
              {convenzioneEntiList.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nessun ente associato a questa convenzione</p>
                  <p className="text-sm mt-2">Vai nel tab "Enti" e modifica la convenzione per ogni ente</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ente</TableHead>
                        <TableHead>Regione</TableHead>
                        <TableHead>Piano</TableHead>
                        <TableHead className="text-right">Prezzo Conv.</TableHead>
                        <TableHead className="text-right">Revenue Naz.</TableHead>
                        <TableHead className="text-right">Fee</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {convenzioneEntiList.map((ente) => (
                        <TableRow key={ente.ente_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{ente.denominazione}</p>
                              <p className="text-xs text-gray-500">{ente.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>{ente.regione || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={ente.piano === 'pro' ? 'default' : 'secondary'}>
                              {ente.piano?.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            €{ente.prezzo_convenzione?.toFixed(2) || '0.00'}
                          </TableCell>
                          <TableCell className="text-right text-purple-700">
                            €{ente.revenue_nazionale?.toFixed(2) || '0.00'}
                          </TableCell>
                          <TableCell className="text-right text-orange-700">
                            €{ente.fee_nazionale?.toFixed(2) || '0.00'}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => rimuoviEnteConvenzione(showConvenzioneEntiModal.convenzione_id, ente.ente_id)}
                            >
                              <UserX className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Summary */}
                  <div className="mt-4 pt-4 border-t flex gap-6 justify-end">
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Totale Revenue</p>
                      <p className="font-bold text-purple-700">
                        €{convenzioneEntiList.reduce((sum, e) => sum + (e.revenue_nazionale || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Totale Fee</p>
                      <p className="font-bold text-orange-700">
                        €{convenzioneEntiList.reduce((sum, e) => sum + (e.fee_nazionale || 0), 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="p-6 border-t flex justify-end">
              <Button variant="outline" onClick={() => setShowConvenzioneEntiModal(null)}>
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
